import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {Popover} from 'antd';
import {MessageOutlined, RightOutlined} from '@ant-design/icons';
import {GET_AWAITING_REPLY_TICKETS} from 'graphql/queries/aiHelpdesk';
import {useHasSupportAccess, useIsSuperAdmin} from 'hooks';
import {useVisibleSubTabs} from 'utils/useVisibleSubTabs';
import {NavTabsEnum} from 'utils/constants';
import {toAwaitingReplyItems} from '../helpdeskTicketsUtils';
import {TicketUserAvatar} from './TicketUserAvatar';
import styles from './AwaitingReplyFab.module.scss';

// Same cadence as the ticket alerts watcher; a customer push also refetches
// this through refreshTicketCounts.
const POLL_MS = 22000;
const LIST_LIMIT = 20;

const VARIABLES = {
  input: {
    assignedToMe: true,
    awaitingReply: true,
    pagination: {paginate: true, page: 1, limit: LIST_LIMIT, offset: 0},
  },
  // Only the latest event, for the preview line.
  eventsInput: {pagination: {page: 1, limit: 1}, sort: {direction: 'DESC'}},
};

const AwaitingList = ({items, total, onOpen, onViewAll}) => (
  <div className={styles.panel}>
    <div className={styles.panelHead}>
      <span className={styles.panelTitle}>Awaiting your reply</span>
      <span className={styles.panelCount}>{total}</span>
    </div>
    <p className={styles.panelHint}>
      Customers waiting on you, longest wait first. Open one to reply.
    </p>
    <ul className={styles.list}>
      {items.map(item => (
        <li key={item.id}>
          <button
            type="button"
            className={styles.item}
            onClick={() => onOpen(item.id)}>
            <TicketUserAvatar
              imageUrl={item.userImageUrl}
              initials={item.userInitials}
              size="lg"
            />
            <span className={styles.itemBody}>
              <span className={styles.itemTop}>
                <span className={styles.itemName}>{item.userDisplayName}</span>
                {item.awaitingReply && (
                  <span
                    className={`${styles.wait} ${
                      styles[item.awaitingReply.color] || ''
                    }`}>
                    {item.awaitingReply.label}
                  </span>
                )}
              </span>
              <span className={styles.itemIssue}>
                {item.intentCategory !== '—' ? item.intentCategory : 'Ticket'}
              </span>
              <span className={styles.itemMessage}>
                {item.lastMessage
                  ? `${
                      item.lastMessageSender
                        ? `${item.lastMessageSender}: `
                        : ''
                    }${item.lastMessage}`
                  : 'New message'}
              </span>
            </span>
            <RightOutlined className={styles.chevron} />
          </button>
        </li>
      ))}
    </ul>
    {total > items.length && (
      <button type="button" className={styles.viewAll} onClick={onViewAll}>
        View all {total} in Awaiting Your Reply
      </button>
    )}
  </div>
);

/**
 * Floating button above "Add to shortcuts" that appears only while customers
 * are waiting on the signed-in agent. Hover or click lists them; picking one
 * opens that ticket's conversation.
 */
const AwaitingReplyFabInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const adminId = useSelector(state => state?.auth?.userData?.user?.id);
  // The shortcuts button is super-admin only; sit above it when it's there.
  const stacked = useIsSuperAdmin();
  const [open, setOpen] = useState(false);

  const {data, previousData} = useQuery(GET_AWAITING_REPLY_TICKETS, {
    variables: VARIABLES,
    pollInterval: POLL_MS,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  // The ticket open on screen is already being answered: leave it out, so the
  // button goes away (and stops covering the reply box) when it was the only
  // one waiting.
  const viewingId = location.pathname.match(
    /\/ai-helpdesk\/ticket\/([^/]+)/,
  )?.[1];
  // A failed poll keeps the last good list rather than hiding the button.
  const all =
    toAwaitingReplyItems(data, adminId) ??
    toAwaitingReplyItems(previousData, adminId) ??
    [];
  const items = all.filter(item => item.id !== viewingId);
  const serverTotal =
    (data ?? previousData)?.getAdminTickets?.pagination?.total ?? all.length;
  const total = serverTotal - (all.length - items.length);

  if (!items.length || !location.pathname.startsWith('/dashboard')) {
    return null;
  }

  const openTicket = id => {
    setOpen(false);
    navigate(`/dashboard/ai-helpdesk/ticket/${id}`, {
      state: {ticketReturnTo: location.pathname + location.search},
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={['hover', 'click']}
      placement="leftBottom"
      overlayClassName={styles.popover}
      content={
        <AwaitingList
          items={items}
          total={total}
          onOpen={openTicket}
          onViewAll={() => {
            setOpen(false);
            navigate('/dashboard/ai-helpdesk/awaiting-reply');
          }}
        />
      }>
      <button
        type="button"
        className={`${styles.fab} ${stacked ? styles.stacked : ''}`}
        aria-label={`${total} customer${
          total === 1 ? '' : 's'
        } awaiting your reply`}>
        <span className={styles.pulse} />
        <MessageOutlined className={styles.icon} />
        <span className={styles.badge}>{total > 99 ? '99+' : total}</span>
      </button>
    </Popover>
  );
};

// "Awaiting Your Reply" is a slice of My Tickets and rides on that grant (see
// AiHelpDeskLayout), so the button follows the same rule as the tab.
const MY_TICKETS = [{key: 'my-tickets'}];

/**
 * Mounts the polling part only for admins who can see the Awaiting Your Reply
 * tab: Customer Support view access plus the My Tickets sub-tab.
 */
const AwaitingReplyFab = () => {
  const hasSupportAccess = useHasSupportAccess();
  const {isVisible} = useVisibleSubTabs(
    NavTabsEnum.CustomerSupport,
    MY_TICKETS,
  );
  return hasSupportAccess && isVisible('my-tickets') ? (
    <AwaitingReplyFabInner />
  ) : null;
};

export default AwaitingReplyFab;
